import { Injectable } from '@angular/core';
import { LoggerService } from '../../../core/services/logger.service';
import { UtilService } from '../../../core/services/util.service';
import { GerentesPFSessionDataDTO } from './GerentesPFSessionDataDTO';
import { TicketCreationRequestControlService } from 'src/app/shared/services/ticket-creation-request-control.service';
import {
  CIFDatadogService,
  CloseTicketByEventProps,
} from 'src/app/shared/services/cif-datadog';
import { MediaType } from 'src/app/utils/media-type';
import { isDigital } from 'src/app/utils/cxone-utils';
import { LocalStorageUtils } from 'src/app/data/CXOne/util/LocalStorageUtils';
import { JsonConverterUtils } from 'src/app/data/CXOne/util/JsonConverterUtils';

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type Ticket = { id_interacao: string | null; id_ticket: string | null };

export interface TemplateValuesChannelProviderGerentesPF {
  fieldNegocio: string;
  triggerPlugin: string;
  templateAusente: string;
  templateSessaoPadrao: string;
  templateCreateSession: string;
  retrieveRecordTable: string;
  retrieveRecordCol: string;
  negocio: string;
}

@Injectable({
  providedIn: 'root',
})
export class DynamicsGerentePfService {
  private readonly RECALL_LIMIT = 50;
  private readonly RECALL_DELAY_MS = 2000;
  private readonly tableName: string = 'itau_interacao';
  private readonly retrieveRecordTable = 'iresolve_itau_interacao';
  private readonly retrieveRecordCol = 'iresolve_atnd_grntid';
  public templateValuesChannelProvider: TemplateValuesChannelProviderGerentesPF;
  private messageHandler: (event: Event) => void;

  constructor(
    private logger: LoggerService,
    private utilService: UtilService,
    private ticketCreationRequestControlService: TicketCreationRequestControlService,
    private cifDatadogService: CIFDatadogService,
    private localStorageUtils: LocalStorageUtils,
    private jsonConverterUtils: JsonConverterUtils,
  ) {
    this.templateValuesChannelProvider = {
      fieldNegocio: 'iresolve_st_plugin',
      triggerPlugin: 'iresolve_st_plugin',
      templateAusente: 'rc6_nice_ausente',
      templateSessaoPadrao: 'rc6_sessao_padrao',
      templateCreateSession: 'rc6_sessao_atendimento_cc',
      retrieveRecordTable: 'iresolve_itau_interacao',
      retrieveRecordCol: 'iresolve_atnd_grntid',
      negocio: 'juridico',
    };
    this.messageHandler = this.DynamicsMessageHandler.bind(this);
  }

  async initialize(cifUrl: string): Promise<void> {
    this.logger.info(`DYNAMICS - inicializando CIF: ${cifUrl}`);
    this.utilService.getIframeEnvironment();
    const script = document.createElement('script');

    script.src = cifUrl;
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('data-crmurl', cifUrl);
    script.setAttribute('data-cifid', 'CIFMainLibrary');

    document.body.appendChild(script);

    return new Promise<void>((resolve, reject) => {
      const onCIFInitDone = async (event: Event) => {
        this.logger.info('DYNAMICS - CIF inicializado', event);
        try {
          this.logger.info('DYNAMICS - CIF Iframe setado para 400 pixels');

          await (window as any).Microsoft.CIFramework.setMode(1);

          await (window as any).Microsoft.CIFramework.addHandler(
            'onmodechanged',
            async (eventData: any) => {
              this.logger.info('DYNAMICS - CIF Mode Changed:', eventData);

              await (window as any).Microsoft.CIFramework.setMode(1);
            },
          );

          await (window as any).Microsoft.CIFramework.setWidth(400);
        } catch (error) {
          this.logger.error('DYNAMICS - Erro ao inicializar CIF', error);
          reject(error);
        } finally {
          resolve();
          window.removeEventListener('CIFInitDone', onCIFInitDone);
        }
      };

      window.addEventListener('CIFInitDone', onCIFInitDone);
    });
  }

  public async loadTemplateValues(): Promise<TemplateValuesChannelProviderGerentesPF | null> {
    const channelProviderVariablesRaw = await (
      window as any
    ).Microsoft.CIFramework.getEnvironment();

    this.logger.info('channel provider raw', channelProviderVariablesRaw);
    if (channelProviderVariablesRaw) {
      const channelProviderVariables = this.jsonConverterUtils.JsonParsed(
        channelProviderVariablesRaw,
      );
      this.logger.info(
        'channel provider obj inside if',
        channelProviderVariables,
      );

      let customParams: any = {};
      if (channelProviderVariables.msdyn_customparams) {
        customParams = Object.fromEntries(
          channelProviderVariables.msdyn_customparams
            .split('&')
            .map((kv: string) => kv.split('=')),
        );
      }
      this.logger.info('customparams', customParams);
      const templateValues: TemplateValuesChannelProviderGerentesPF = {
        templateCreateSession:
          customParams.templateCreateSession ||
          this.templateValuesChannelProvider.templateCreateSession,
        templateSessaoPadrao:
          customParams.templateSessaoPadrao ||
          this.templateValuesChannelProvider.templateSessaoPadrao,
        templateAusente:
          customParams.templateAusente ||
          this.templateValuesChannelProvider.templateAusente,
        fieldNegocio:
          customParams.fieldNegocio ||
          this.templateValuesChannelProvider.fieldNegocio,
        triggerPlugin:
          customParams.triggerPlugin ||
          this.templateValuesChannelProvider.triggerPlugin,
        retrieveRecordTable:
          customParams.retrieveRecordTable ||
          this.templateValuesChannelProvider.retrieveRecordTable,
        retrieveRecordCol:
          customParams.retrieveRecordCol ||
          this.templateValuesChannelProvider.retrieveRecordCol,
        negocio:
          customParams.negocio || this.templateValuesChannelProvider.negocio,
      };

      this.templateValuesChannelProvider = templateValues;

      this.logger.info('DYNAMICS - Template Values Loaded:', templateValues);

      return templateValues;
    } else {
      this.logger.error(
        'Erro ao carregar environment do CIFramework:',
        channelProviderVariablesRaw,
      );
      return null;
    }
  }

  public async cifCreateSession(
    input_template: any,
    mediaType: MediaType | null,
    masterId: string,
  ): Promise<string> {
    let interactionId = null;
    try {
      interactionId = input_template['templateParameters'].id_interacao;
      this.cifDatadogService.openTicket.session.request({
        interactionId,
        template: input_template,
        mediaType,
        masterId,
      });

      const canCreate = await (
        window as any
      ).Microsoft.CIFramework.canCreateSession();
      if (canCreate) {
        const sessionId = await (
          window as any
        ).Microsoft.CIFramework.createSession(input_template);

        this.logger.info('DYNAMICS - sessionId do createSession', sessionId);
        this.cifDatadogService.openTicket.session.success({
          sessionId: sessionId,
          interactionId: interactionId,
          mediaType,
          masterId,
        });

        return sessionId;
      }

      this.logger.warn(
        'DYNAMICS - Não é possível criar uma sessão com o template fornecido.',
      );
      throw new Error();
    } catch (error: unknown) {
      this.logger.error('DYNAMICS - Erro ao criar sessão', error);
      this.cifDatadogService.openTicket.session.fail({
        interactionId,
        template: input_template,
        mediaType,
        masterId,
        error,
      });
      throw error; // Re-throw the error to be caught by the caller
    }
  }

  public async cifCloseSession(
    input_template: string,
    context: any,
    sessionId: string,
    contactId: string,
    mediaType: MediaType,
  ): Promise<void> {
    let idInteracao = null;
    try {
      const updateResult = await (
        window as any
      ).Microsoft.CIFramework.updateContext(context, sessionId);

      const input = {
        templateName: input_template,
        templateParameters: {},
      };

      idInteracao = updateResult?.id_interacao;

      if (!sessionId || !idInteracao) {
        this.logger.warn(
          'CIVICS - sessionId ou idInteracao ausentes, não é possível fechar a sessão.',
        );
        return;
      }

      const closeEventLogDetails: CloseTicketByEventProps = {
        sessionId: sessionId,
        interactionId: idInteracao,
        mediaType,
        masterId: contactId,
      };

      this.cifDatadogService.closeTicket.start(
        'fecharSessao',
        closeEventLogDetails,
      );

      await (window as any).Microsoft.CIFramework.notifyEvent(input);

      // Chama a função para motivar o encerramento com o id da interação
      const eventInputParameters = {
        sessionId: sessionId,
        correlationId: idInteracao,
      };

      await (window as any).Microsoft.CIFramework.raiseEvent(
        'fecharSessao',
        eventInputParameters,
      );

      this.cifDatadogService.closeTicket.success(
        'fecharSessao',
        closeEventLogDetails,
      );

      this.logger.info('CIVICS - sessão fechada com sucesso', sessionId);
    } catch (error) {
      this.logger.error(
        'CIVICS - Erro ao fechar sessão: ',
        error,
        'sessionId:',
        sessionId,
      );
      this.cifDatadogService.closeTicket.fail('fecharSessao', {
        sessionId: sessionId,
        interactionId: idInteracao,
        error,
        mediaType,
        masterId: contactId,
      });
      throw error;
    }
  }

  public async cifCreateTicket(
    attachData: any,
    mediaType: MediaType,
    contactId: string,
  ): Promise<Ticket> {
    let retorno: Ticket = {
      id_interacao: null,
      id_ticket: null,
    };
    // this.cifDatadogService.openTicket.initialData({
    //   attachData,
    //   masterId: contactId,
    //   mediaType,
    // });

    const data = attachData.itau_mst_attachdata;

    if (isDigital(mediaType)) {
      this.ticketCreationRequestControlService.clearUpLastTicketCreationRequest();
    }

    try {
      this.cifDatadogService.openTicket.request.create({
        attachData: data,
        mediaType,
        masterId: contactId,
      });

      this.logger.info('attachDataBefore - ', attachData);
      this.logger.info('attachDataString - ', JSON.stringify(attachData));
      const result = await (window as any).Microsoft.CIFramework.createRecord(
        this.tableName,
        JSON.stringify(attachData),
      );

      this.cifDatadogService.openTicket.request.success({
        attachData: data,
        mediaType,
        masterId: contactId,
      });

      this.logger.info(`DYNAMICS - ID Interação criado: ${result}`);
      retorno.id_interacao = this.jsonConverterUtils.JsonParsed(result).id;

      //recuperacao do ID do ticket
      //remove this
      this.logger.info('retorno', retorno);
      if (retorno.id_ticket == null && retorno.id_interacao != null) {
        //remove this
        this.logger.info('before CifRetrive');
        retorno.id_ticket = await this.cifRetrieveRecord(
          retorno.id_interacao,
          this.tableName,
          mediaType,
          contactId,
        );
        this.logger.info(`DYNAMICS - Ticket recuperado: ${retorno.id_ticket}`);
      }
    } catch (error) {
      this.cifDatadogService.openTicket.request.fail({
        attachData: data,
        mediaType,
        masterId: contactId,
        error,
      });
      throw new Error(
        `Erro ao criar interação ou recuperar ticket: ${error instanceof Error ? error.message : error}`,
      );
    }
    return retorno;
  }

  private async cifRetrieveRecord(
    interactionId: string,
    tableName: string,
    mediaType: MediaType,
    masterId: string,
  ): Promise<string | null> {
    let idTicket = null;
    this.cifDatadogService.openTicket.retrieve.start({
      interactionId,
      mediaType,
      masterId,
    });

    for (let attempt = 0; attempt < this.RECALL_LIMIT; attempt++) {
      await this.delay(this.RECALL_DELAY_MS);

      const options = `?$select=itau_protocolo_interacao&$expand=${this.templateValuesChannelProvider.retrieveRecordTable}($select=${this.templateValuesChannelProvider.retrieveRecordCol})`;

      //remove this
      this.logger.info('Options', options);
      try {
        const result = await (
          window as any
        ).Microsoft.CIFramework.retrieveRecord(
          tableName,
          interactionId,
          options,
        );
        const interaction = this.jsonConverterUtils.JsonParsed(result);
        this.logger.info('interaction', JSON.stringify(interaction));

        if (
          interaction &&
          interaction[
            this.templateValuesChannelProvider.retrieveRecordTable
          ]?.[0] != null
        ) {
          idTicket =
            interaction[
              this.templateValuesChannelProvider.retrieveRecordTable
            ][0][this.templateValuesChannelProvider.retrieveRecordCol];

          this.logger.info(
            `DYNAMICS - Ticket ID encontrado na tentativa ${attempt + 1}: ${idTicket}`,
          );
          this.cifDatadogService.openTicket.retrieve.success({
            interactionId,
            mediaType,
            masterId,
          });
          break;
        }
      } catch (error) {
        this.cifDatadogService.openTicket.retrieve.fail({
          interactionId,
          mediaType,
          masterId,
        });
        throw new Error(
          `Erro ao recuperar ticket na tentativa ${attempt + 1}: ${error instanceof Error ? error.message : error}`,
        );
      }
    }

    if (!idTicket) {
      this.cifDatadogService.openTicket.retrieve.excessiveAttemptsError({
        interactionId,
        mediaType,
        masterId,
        attempts: this.RECALL_LIMIT,
      });
      this.logger.warn(
        `DYNAMICS - Limite de tentativas (${this.RECALL_LIMIT}) atingido ao recuperar o Ticket ID para a interação ${interactionId}`,
      );
    }

    return idTicket;
  }

  public async changeSessionFocus(
    ContactCenterInteractionId: string,
  ): Promise<void> {
    const sessionRelationship = this.getSessionRelationship(
      ContactCenterInteractionId,
    );

    this.logger.info(
      'Chamando raise event ControleFocarSessao com os valores: ',
      sessionRelationship,
    );

    await (window as any).Microsoft.CIFramework.raiseEvent(
      'ControleFocarSessao',
      {
        sessionId: sessionRelationship?.dynamicsSessionId,
        correlationId: sessionRelationship?.dynamicsinteractionId,
      },
    );
  }

  private getSessionRelationship(
    ContactCenterInteractionId: string,
  ): GerentesPFSessionDataDTO | null {
    const value = this.localStorageUtils.safeGetItem(
      `sessionRelationship-${ContactCenterInteractionId}`,
    );
    if (value) {
      return this.jsonConverterUtils.JsonParsed(value);
    }
    return null;
  }

  public DynamicsMessageHandler(e: any) {
    if (e.data.messageType === 'onSessionClosed') {
      this.OnSessionClosedHandlerFromWindow(e);
    }

    if (e.data.messageType === 'onSessionSwitched') {
      this.OnSessionSwitchHandlerFromWindow(e);
    }
  }
  public async OnSessionSwitchHandlerFromWindow(e: any) {
    this.logger.info(
      'Troca de sessão solicitada no dynamics via message',
      JSON.stringify(e),
    );

    if (e.data.messageData.focused === true) {
      return;
    }

    let ContactCenterInteractionId =
      this.localStorageUtils.safeGetItem('interactionFocused') ?? '';
    const sessionRelationship = this.getSessionRelationship(
      ContactCenterInteractionId,
    );

    let controleFocarSessao = {
      sessionId: sessionRelationship?.dynamicsSessionId,
      correlationId: sessionRelationship?.dynamicsinteractionId,
    };

    this.logger.info(
      'Solicitacao retorno a sessão do dynamics.',
      JSON.stringify(controleFocarSessao),
    );
    await (window as any).Microsoft.CIFramework.raiseEvent(
      'ControleFocarSessao',
      controleFocarSessao,
    );
  }

  public async OnSessionClosedHandlerFromWindow(e: any) {
    this.logger.info(
      'Encerramento de sessão solicitada no dynamics.',
      JSON.stringify(e.data),
    );
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
